import { getCalendarFormat, getDate, TEmailReport, TNumStr, TTimeInterval } from "@cakemail-org/ui-components-v2"
import { logError } from "./data"
import moment, { DurationInputArg2 } from "moment"
import { t } from "i18next"
import { TAPIDateRangeReturn } from "../types/dates"

export function isUnixRange(range: string) {
    return /^\d{10}-\d{10}$/.test(range)
}


export function getIntervalBasedOnTimeRange(range: string, maxPoints: number = 180): TTimeInterval {
    let totalMinutes = 0
    const INTERVALS: Record<TTimeInterval, number> = {
        "5m": 5,
        "15m": 15,
        "30m": 30,
        "1h": 60,
        "1d": 1440,
    }
    // Unix timestamp range "1700000000-1700003600"
    if (isUnixRange(range)) {
        const [start, end] = range.split("-").map(Number)
        if (start >= end) console.error("Invalid timestamp range: start must be before end.")
        totalMinutes = (end - start) / 60
    } // Duration format "1d", "3h", "45m"
    else {
        const match = range.match(/^(\d+)([smhd])$/)
        if (!match) throw new Error("Invalid range format. Use '1d', '3h', '30m' OR 'unix-start-unix-end'")

        const value = parseInt(match[1], 10)
        const unit = match[2]

        switch (unit) {
            case "s":
                totalMinutes = value / 60
                break
            case "m":
                totalMinutes = value
                break
            case "h":
                totalMinutes = value * 60
                break
            case "d":
                totalMinutes = value * 1440
                break
        }
    }
    // Find the best interval that keeps the number of points within limits
    let bestInterval: TTimeInterval = "5m"
    for (const [interval, minutes] of Object.entries(INTERVALS) as [TTimeInterval, number][]) {
        const pointCount = totalMinutes / minutes
        // Select the first interval where pointCount is within the allowed maxPoints
        if (pointCount <= maxPoints) {
            bestInterval = interval
            break
        }
    }
    return bestInterval
}

export function formatDelta(unixTimeStamp1: number, unixTimeStamp2: number): string {
    let deltaInSeconds = unixTimeStamp1 - unixTimeStamp2
    const minute = 60
    const hour = 60 * minute
    const day = 24 * hour
    const year = 365 * day

    // Calculate each unit of time
    const years = Math.floor(deltaInSeconds / year)
    deltaInSeconds %= year

    const days = Math.floor(deltaInSeconds / day)
    deltaInSeconds %= day

    const hours = Math.floor(deltaInSeconds / hour)
    deltaInSeconds %= hour

    const minutes = Math.floor(deltaInSeconds / minute)
    deltaInSeconds %= minute

    const units = []
    if (years > 0) units.push(`${years}${t("general-year-abridge", { count: years })}`)
    if (days > 0) units.push(`${days}${t("general-day-abridge", { count: days })}`)
    if (hours > 0) units.push(`${hours}${t("general-hour-abridge", { count: hours })}`)
    if (minutes > 0) units.push(`${minutes}${t("general-minute-abridge", { count: minutes })}`)
    if (deltaInSeconds > 0 || units.length === 0) units.push(`${deltaInSeconds}${t("general-second-abridge", { count: deltaInSeconds })}`)

    return units.join(" ")
}

export function getSlicesBetweenUnixTimes(
    startUnix: number,
    endUnix: number,
    interval: TTimeInterval
): number {
    const intervalInSeconds = parseIntervalToUnix(interval)
    if (endUnix <= startUnix) {
        throw new Error("End time must be greater than start time")
    }
    const totalDuration = endUnix - startUnix
    const slices = Math.floor(totalDuration / intervalInSeconds)

    return slices + 1
}

export function parseIntervalToUnix(interval: TTimeInterval): number {
    const unit = interval.slice(-1) // Get the last character (e.g., "m", "h", "d")
    const value = parseInt(interval.slice(0, -1), 10) // Get the numeric part

    switch (unit) {
        case "m": // Minutes
            return value * 60
        case "h": // Hours
            return value * 60 * 60
        case "d": // Days
            return value * 24 * 60 * 60
        default:
            throw new Error("Invalid interval format")
    }
}

export function getAPIDateRange({ range, rangeMultiplier }: { range: string, rangeMultiplier?: number }): TAPIDateRangeReturn {
    function multiplyRange(date: number, mRange?: number, rangeFormat?: string) {
        const now = moment()
        const startDiff = mRange ? mRange : now.diff(moment.unix(date), rangeFormat as DurationInputArg2 || 'ms')

        const multiplier = rangeMultiplier ? rangeMultiplier : 2
        return now.subtract(multiplier > 2 ? startDiff * multiplier : startDiff, rangeFormat as DurationInputArg2 || "ms").unix()
    }

    const useFullRange = isUnixRange(range)
    if (useFullRange) {
        const [start, end] = range.split("-").map(Number)
        if (start >= end) console.error("Invalid timestamp range: start must be before end.")

        let startDate = getDate({ date: start, format: "unix" })
        let endDate = getDate({ date: end, format: "unix" })
        if (rangeMultiplier) {
            startDate = multiplyRange(startDate)
            endDate = multiplyRange(endDate)
        }

        return {
            start: startDate,
            end: endDate,
            timeAgo: `${getCalendarFormat({ unixDate: start })} ${t("general-to")} ${getCalendarFormat({ unixDate: end })}`,
            rangeFormat: "range"
        }
    }

    const match = range.match(/^(\d+)([smhd])$/)
    if (!match) throw new Error("Invalid range format. Use e.g., '1d', '20h', '30m'")

    const value = parseInt(match[1], 10)
    const rangeFormat = match[2]

    const dateRange = getDate().subtract(rangeMultiplier ? rangeMultiplier * value : value, rangeFormat as DurationInputArg2)

    let rangeFormatString: "days" | "minutes" | "hours" | "range" = "days"
    switch (rangeFormat) {
        case "m":
            rangeFormatString = "minutes"
            break
        case "h":
            rangeFormatString = "hours"
            break
        default:
            rangeFormatString = "days"
            break
    }
    let startDate = dateRange.unix()
    let endDate = rangeMultiplier && rangeMultiplier > 1 ? getDate().subtract((rangeMultiplier - 1) * value, rangeFormat as DurationInputArg2).unix() : getDate({ format: "unix" })

    return {
        start: startDate,
        end: endDate,
        timeAgo: getDate({ date: endDate }).diff(dateRange, rangeFormat as DurationInputArg2 || "day") + 1,
        rangeFormat: rangeFormatString
    }
}